/** Mirrors of the C# budget plan DTO records, field for field. */

/**
 * Where a plan sits in the budget cycle.
 *
 * The last three are set by **Budget Review** and nothing in this module can reach them yet.
 * They are declared because they are the shared vocabulary that module will use, and because
 * the status facet emits every value whatever its count.
 */
export const BUDGET_PLAN_STATUS = {
  draft: 0,
  submissionOpen: 1,
  submissionClosed: 2,
  underReview: 3,
  finalized: 4,
  rejected: 5,
} as const;

/*
  THERE IS NO TONE MAP HERE, and the absence is the decision.

  A plan carries TWO state concepts, and conflating them is the trap: its LIFECYCLE (draft,
  submission open, under review…) and its ROW STATE (live or deleted). The disciplinary-record
  panel already settles this — it "carries two state columns, deliberately: the case status as
  plain text, and the row's own live/deleted marker as the `status: true` column the shell
  hoists to the end."

  So the lifecycle renders as PLAIN TEXT from `statusLabel`, and only live-or-deleted gets a
  pill. That also sidesteps a tone problem with no honest answer: there are four tones, `pending`
  is refused on an index table because amber is the brand colour, and `blocked` means a state
  that stops the record being used — which "submission closed" is not. Three of the six
  lifecycle states would have shared one tone and said nothing the label had not already said.
*/

/**
 * Row shape for the budget plan list.
 *
 * **No total budget and no PPA count** — both are sums over a department's PPAs, and that
 * table does not exist yet. They arrive with the PPA, not before it.
 */
export interface BudgetPlanListItem {
  id: string;
  planNumber: string;
  fiscalYear: number;
  /** Calendar dates — `YYYY-MM-DD`, never an instant. */
  submissionClosesAt: string;
  coverageStartDate: string;
  coverageEndDate: string;
  status: number;
  /** The status in words, resolved server-side. The int is what a facet selection uses. */
  statusLabel: string;
  /**
   * Whether the plan's own frame may still be edited — Draft or Submission-Open.
   *
   * Derived server-side and sent, so the form's disabled reason and the endpoint's refusal
   * cannot disagree. Never re-derived from `status` on this side.
   */
  isEditable: boolean;
  description: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

/**
 * One plan, and nothing under it — what the profile reads.
 *
 * **There is no goal selection**, and the empty shape is the decision (23 Sep 2026). A plan
 * adopted a subset of the catalog and a PPA was refused a goal outside it; the Goal Setting
 * tab is the CATALOG now — sector, sub-sector, goal — and every plan may use all of it.
 */
export interface BudgetPlanDetail {
  plan: BudgetPlanListItem;
}

/**
 * Create and update take the same shape — the plan's HEADER, and nothing else.
 *
 * Five fields, which is the reference's own create payload exactly: its form collects the
 * year, the window, the coverage and a description, and never a goal. Setting up the goals is
 * its own process on the plan's own tab, after the plan exists.
 */
export interface BudgetPlanRequest {
  fiscalYear: number;
  submissionClosesAt: string;
  coverageStartDate: string;
  coverageEndDate: string;
  description: string | null;
  notes: string | null;
}
